import React, { useState } from 'react';
import { View, Pressable, ScrollView } from 'react-native';
import { Text } from '@/components/ui/text';
import { CUISINE_OPTIONS, DIETARY_OPTIONS } from '../constants';

interface TastePreferencesScreenProps {
  onSave: (cuisines: string[], dietaryRestrictions: string[]) => Promise<void>;
  onNext: () => Promise<void>;
  initialCuisines: string[];
  initialRestrictions: string[];
  isAdvancing: boolean;
}

export function TastePreferencesScreen({
  onSave,
  onNext,
  initialCuisines,
  initialRestrictions,
  isAdvancing,
}: TastePreferencesScreenProps) {
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>(initialCuisines);
  const [selectedRestrictions, setSelectedRestrictions] = useState<string[]>(initialRestrictions);

  const toggleCuisine = (cuisine: string) => {
    setSelectedCuisines((prev) =>
      prev.includes(cuisine)
        ? prev.filter((c) => c !== cuisine)
        : [...prev, cuisine]
    );
  };

  const toggleRestriction = (restriction: string) => {
    setSelectedRestrictions((prev) =>
      prev.includes(restriction)
        ? prev.filter((r) => r !== restriction)
        : [...prev, restriction]
    );
  };

  const handleContinue = async () => {
    await onSave(selectedCuisines, selectedRestrictions);
    await onNext();
  };

  const canContinue = selectedCuisines.length > 0;

  return (
    <View className="flex-1 px-6 pt-4">
      <Text className="text-xl font-bold text-foreground mb-2">Taste Preferences</Text>
      <Text className="text-sm text-muted-foreground mb-4">
        Select cuisines you enjoy. This helps filter search results and populate your profile.
      </Text>

      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        <Text className="text-base font-semibold text-foreground mb-2">
          Cuisines (select at least one)
        </Text>
        <View className="flex-row flex-wrap gap-2 mb-6">
          {CUISINE_OPTIONS.map((cuisine) => (
            <Pressable
              key={cuisine}
              testID={`cuisine-${cuisine}`}
              onPress={() => toggleCuisine(cuisine)}
              className={`rounded-full border px-3 py-1.5 ${
                selectedCuisines.includes(cuisine) ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <Text className={`text-sm ${
                selectedCuisines.includes(cuisine) ? 'text-primary font-semibold' : 'text-foreground'
              }`}>
                {cuisine}
              </Text>
            </Pressable>
          ))}
        </View>

        <Text className="text-base font-semibold text-foreground mb-2">
          Dietary Restrictions (optional)
        </Text>
        <View className="flex-row flex-wrap gap-2 mb-6">
          {DIETARY_OPTIONS.map((restriction) => (
            <Pressable
              key={restriction}
              testID={`dietary-${restriction}`}
              onPress={() => toggleRestriction(restriction)}
              className={`rounded-full border px-3 py-1.5 ${
                selectedRestrictions.includes(restriction) ? 'border-primary bg-primary/10' : 'border-border'
              }`}
            >
              <Text className={`text-sm ${
                selectedRestrictions.includes(restriction) ? 'text-primary font-semibold' : 'text-foreground'
              }`}>
                {restriction}
              </Text>
            </Pressable>
          ))}
        </View>
      </ScrollView>

      <Pressable
        onPress={handleContinue}
        disabled={!canContinue || isAdvancing}
        accessibilityLabel="Continue"
        className="bg-primary rounded-lg py-3 px-8 mb-4"
        style={{ opacity: !canContinue || isAdvancing ? 0.5 : 1 }}
      >
        <Text className="text-primary-foreground text-base font-semibold text-center">
          Continue
        </Text>
      </Pressable>
    </View>
  );
}
